import React, { useEffect, useCallback } from "react";
import {
  AdvancedMarker,
  ControlPosition,
  MapControl,
  useMap,
} from "@vis.gl/react-google-maps";
import { MockMarker, MockPolygon, MockPolyline, Toolbar } from "./toolbar";

import LabelMarker from "./components/LabelMarker";
import MapArrow from "./components/MapArrow";
import { useMatch, useParams } from "react-router-dom";
import SurfaceFeatures from "./components/SurfaceFeatures";
import { usePolygonHole } from "./contexts/PolygonHoleContext";
import {
  useData,
  LabelMarkerType,
  ArrowData,
  HazardMarkerType,
} from "./contexts/DataContext";
import { useMapEditing } from "./utils/routing";
import {
  SiteMapSurfaceType,
  SiteMapFeatureType,
  SurfaceType,
} from "./DataAndMap";
import HazardMarker from "./components/HazardMarker";
import { COLOR_RED, COLOR_BLUE, COLOR_BLACK } from "./utils/colors";
import MapBoundaryLine from "./components/MapBoundaryLine";
import { useToolbarState } from "./contexts/ToolbarStateContext";
import * as turf from "@turf/turf";
import type { Position } from "geojson";
import useNavigateKeepSearchParams from "../../hooks/useNavigateKeepSearchParams";
export type SimpleLatLng = { lat: number; lng: number };

export type SiteMapSurfaceFeatureType =
  | "parkingLot"
  | "snowPile"
  | "other"
  | "muniSidewalk"
  | "propertySidewalk"
  | "lawn"
  | "landscapeBed"
  | "rockBed"
  | "boundary"
  | "hole";
export type CreateMode = "arrow" | "hazard" | "polygon" | "text";
type CreateModeData = MockPolygon | MockMarker | MockPolyline;
type CreateModeExtra = SiteMapSurfaceFeatureType;

const isArrow = (data: CreateModeData): data is MockPolyline =>
  "getPath" in data;
const isMarker = (data: CreateModeData): data is MockMarker =>
  "getPosition" in data;
const isPolygon = (data: CreateModeData): data is MockPolygon =>
  "getPath" in data;

// Create a fingerprint based on the actual data content to detect true duplicates
const fingerprintData = (
  mode: CreateMode,
  data: CreateModeData,
  extra?: CreateModeExtra,
) => {
  if (mode === "polygon" && isPolygon(data)) {
    const coordinates = data
      .getPath()
      .getArray()
      .map((latLng) => `${latLng.lat().toFixed(8)},${latLng.lng().toFixed(8)}`)
      .join("|");
    return `${mode}-${coordinates}-${extra || "parkingLot"}`;
  } else if (
    (mode === "text" && isMarker(data)) ||
    (mode === "hazard" && isMarker(data))
  ) {
    const position = data.getPosition();
    return position
      ? `${mode}-${position.lat().toFixed(8)},${position.lng().toFixed(8)}`
      : `${mode}-${Math.random()}`; // fallback for markers without position
  } else if (mode === "arrow" && isArrow(data)) {
    const coordinates = data
      .getPath()
      .getArray()
      .map((latLng) => `${latLng.lat().toFixed(8)},${latLng.lng().toFixed(8)}`)
      .join("|");
    return `${mode}-${coordinates}-${extra || "parkingLot"}`;
  } else {
    return `${mode}-${Math.random()}`; // fallback for other modes
  }
};

export type SimpleFeatureVisibility = {
  [key: string]: boolean;
};

/*
 * In various scenarios we have a need to prevent a click event from firing
 * and selecting a surface polygon.
 *
 * Scenarios we've seen so far:
 * - Final click when creating a text label, hazard marker or arrow
 * - Dragging a hazard marker
 *
 * Normally we would prevent those events from firing using event.stopPropagation() but
 * because of the Terra Draw / Google Maps layers we're dealing with here, we don't
 * have easy access to the raw events, so we've taken this approach instead
 */
const preventExtraClick = (preventClickRef: { current: boolean }) => {
  if (preventClickRef.current) return true;
  preventClickRef.current = true;
  // Clear the flag after a short delay
  setTimeout(() => {
    preventClickRef.current = false;
  }, 50);
  return false;
};

const Core = ({
  location,
  showControls,
  surfaceTypes,
  centerOnContent,
}: {
  location: SimpleLatLng;
  showControls: boolean;
  surfaceTypes: SurfaceType[];
  centerOnContent: boolean;
}) => {
  const map = useMap();
  const mapIsReady = map?.getProjection() != null;
  const { editing } = useMapEditing();

  const navigate = useNavigateKeepSearchParams(["return_to"]);
  const selectedSurfaceId = useParams()?.surfaceId;
  const editingBoundaryBase = useMatch({
    path: "/admin/property/:jobId/site-map/:season/edit/boundary",
    caseSensitive: false,
    end: false,
  });
  const settingUpBoundary = useMatch({
    path: "/admin/property/:jobId/site-map/:season/edit/setup-boundary",
    caseSensitive: false,
    end: false,
  });
  const editingBoundary = Boolean(editingBoundaryBase || settingUpBoundary);

  const { shouldCutHole, setShouldCutHole } = usePolygonHole();
  const {
    boundary,
    surfacesVisibility,
    surfaceFeatures,
    addSurfaceFeature,
    deleteSurfaceFeature,
    updateSurfaceFeature,
    hazardMarkers,
    addHazardMarker,
    updateHazardMarker,
    labelMarkers,
    addLabelMarker,
    deleteLabelMarker,
    updateLabelMarker,
    arrows,
    addArrow,
    updateArrow,
    isEmptyMap,
  } = useData();

  const { selectedMode } = useToolbarState();

  const featureVisibility = surfacesVisibility.features.reduce(
    (acc, feature) => {
      acc[feature.value] = feature.visible;
      return acc;
    },
    {} as SimpleFeatureVisibility,
  );

  // Use a ref to track the next ID to avoid conflicts during double-rendering
  const nextIdRef = React.useRef(2); // MUST NOT START AT ZERO!  There is a bug in Google's APIs with removing features with id 0.

  // Set to track processed data fingerprints to prevent duplicates
  const processedEventsRef = React.useRef(new Set());

  // Flag to prevent feature clicks immediately after completing a drawing operation
  const preventClickRef = React.useRef(false);

  const updateFeature = (feature: SiteMapSurfaceType) => {
    updateSurfaceFeature(feature);
  };

  // Update nextIdRef whenever surfaceFeatures changes to ensure we don't have ID conflicts
  useEffect(() => {
    if (surfaceFeatures.length > 0) {
      const maxId = Math.max(...surfaceFeatures.map((feature) => feature.id));
      nextIdRef.current = maxId + 1;
    } else {
      nextIdRef.current = 1;
    }
  }, [surfaceFeatures]);

  // This use Effect is in charge of zooming the map to content if we have any.
  useEffect(() => {
    if (mapIsReady && !isEmptyMap && centerOnContent) {
      const bounds = new google.maps.LatLngBounds();
      for (const surface of surfaceFeatures) {
        for (const coor of surface.coordinates[0]) {
          bounds.extend(coor);
        }
      }
      for (const point of boundary?.coords ?? []) {
        bounds.extend({ lat: point[0], lng: point[1] });
      }
      for (const hazard of hazardMarkers) {
        bounds.extend(hazard);
      }
      for (const label of labelMarkers) {
        bounds.extend(label);
      }
      for (const arrow of arrows) {
        console.log(arrow.coords);
        for (const point of arrow.coords) {
          bounds.extend({ lat: point[0], lng: point[1] });
        }
      }
      // map.setZoom(100);
      map.fitBounds(bounds);
    }
  }, [mapIsReady, isEmptyMap, centerOnContent]); // We only want to run this once when the map is ready. We purposely do not depend on the map features.

  const onComplete = (
    mode: CreateMode,
    data: CreateModeData,
    extra?: CreateModeExtra,
  ) => {
    const dataFingerprint = fingerprintData(mode, data, extra);

    // Check if we've already processed this exact data
    if (processedEventsRef.current.has(dataFingerprint)) {
      return;
    }

    // Mark this data as processed
    processedEventsRef.current.add(dataFingerprint);

    // Clean up old fingerprints after a delay to prevent memory leaks
    setTimeout(() => {
      processedEventsRef.current.delete(dataFingerprint);
    }, 1000);

    if (mode === "hazard" && isMarker(data)) {
      const hazardData = {
        id: nextId(hazardMarkers),
        lat: data?.getPosition()?.lat(),
        lng: data?.getPosition()?.lng(),
        color: COLOR_RED.name,
      };
      addHazardMarker(hazardData);
      navigate(`edit/hazard/${hazardData.id}`);
    }

    if (mode === "text" && isMarker(data)) {
      const labelData = {
        id: nextId(labelMarkers),
        lat: data?.getPosition()?.lat() || 0,
        lng: data?.getPosition()?.lng() || 0,
        text: "Text",
        color: COLOR_BLACK.name,
        size: "md" as const,
      };
      addLabelMarker(labelData);
      navigate(`edit/text/${labelData.id}`);
    }

    if (mode === "arrow" && isArrow(data)) {
      const arrowData = {
        id: nextId(arrows),
        coords: data
          ?.getPath()
          .getArray()
          .map((latLng) => [latLng.lat(), latLng.lng()]),
        color: COLOR_BLUE.name,
      };
      addArrow(arrowData);
      navigate(`edit/arrow/${arrowData.id}`);
    }
    if (mode === "polygon" && isPolygon(data)) {
      const coordinates = data
        .getPath()
        .getArray()
        .map((latLng) => ({
          lat: latLng.lat(),
          lng: latLng.lng(),
        }));
      console.log(
        "first, last",
        coordinates[0],
        coordinates[coordinates.length - 1],
      );

      // if we already have a selected polygon, add this new set of
      // coordinates to it so we have one with holes
      if (selectedSurfaceId && shouldCutHole) {
        console.log("Adding hole to existing polygon:", selectedSurfaceId);
        const existingFeature = surfaceFeatures.find(
          (feature) => feature.id === parseInt(selectedSurfaceId),
        );

        if (existingFeature) {
          const cutHoleResponse = cutHole(existingFeature!, coordinates);

          // Update the existing polygon:
          updateSurfaceFeature(cutHoleResponse.updatedFeature);

          // Depending on the hole, we might have new polygons if we cut the previous one in half:
          for (const newPoly of cutHoleResponse.newFeatures) {
            console.log("Going to add new polygon triggered from a hole cut");
            const newFeatureId = nextIdRef.current++;
            addSurfaceFeature({
              ...newPoly,
              id: newFeatureId,
            });
          }
        }
        setShouldCutHole(false); // Note: if we want to make this mode sticky, we will need to deal with stopping the surface selection navigation.
      } else {
        console.warn("Making a new polygon surface");
        const newFeatureId = nextIdRef.current++;
        const newFeature = {
          id: newFeatureId,
          type: "Polygon" as SiteMapFeatureType,
          coordinates: [coordinates],
          properties: {
            name: "New Polygon",
            surfaceType: extra || "parkingLot",
          },
        };

        addSurfaceFeature(newFeature);
        navigateToSurface(newFeatureId);
      }
    }

    preventExtraClick(preventClickRef);
  };

  const navigateToSurface = (id: number | string) => {
    navigate(`${editing ? "edit/" : ""}surface/${id}`);
  };

  const onFeatureClick = (event: google.maps.Data.MouseEvent) => {
    // Ignore feature click due to recent drawing completion / hazard movement
    if (preventClickRef.current) {
      return;
    }

    // Ignoring feature click because drawing mode is active
    if (selectedMode) {
      return;
    }

    const featureId = event.feature.getProperty("id") as string;
    if (featureId != selectedSurfaceId) {
      navigateToSurface(featureId);
    } else {
      navigate(editing ? "edit" : `.`);
    }
  };

  // Memoize the arrow onSave callback to prevent unnecessary re-renders
  const handleArrowSave = useCallback(
    (arrowData: ArrowData) => {
      updateArrow(arrowData);
    },
    [updateArrow],
  );
  const handleHazardSave = useCallback(
    (hazardData: HazardMarkerType) => {
      preventExtraClick(preventClickRef);
      updateHazardMarker(hazardData);
    },
    [updateHazardMarker],
  );

  const showBaseMarker = !surfaceFeatures.length;

  return (
    <>
      {!editingBoundary && (
        <>
          {showBaseMarker && (
            <AdvancedMarker key={"advanced-marker"} position={location} />
          )}

          {labelMarkers.map((marker) => (
            <LabelMarker
              key={marker.id}
              marker={marker}
              editing={editing}
              onDelete={(marker: LabelMarkerType) => {
                deleteLabelMarker(marker);
              }}
              onMove={(marker: LabelMarkerType) => {
                updateLabelMarker(marker);
              }}
              onSave={(marker: LabelMarkerType) => {
                updateLabelMarker(marker);
              }}
            />
          ))}

          {arrows.map((arrow) => {
            return (
              <MapArrow
                key={arrow.id}
                arrow={arrow}
                editing={editing}
                onSave={handleArrowSave}
              />
            );
          })}

          {hazardMarkers.map((marker) => (
            <HazardMarker
              key={marker.id}
              marker={marker}
              updateHazardMarker={handleHazardSave}
              editing={editing}
            />
          ))}

          {boundary && (
            <MapBoundaryLine boundary={boundary} editing={editing} />
          )}
          <SurfaceFeatures
            surfaceTypes={surfaceTypes}
            editing={editing}
            features={surfaceFeatures}
            featureVisibility={featureVisibility}
            selectedSurfaceId={selectedSurfaceId}
            onFeatureClick={onFeatureClick}
            deleteSurfaceFeature={deleteSurfaceFeature}
            updateSurfaceFeature={updateFeature}
          />
        </>
      )}
      <MapControl position={ControlPosition.TOP_CENTER}>
        {mapIsReady && showControls ? (
          <Toolbar onComplete={onComplete} surfaceTypeOptions={surfaceTypes} />
        ) : null}
        {/* <UndoRedoControl drawingManager={drawingManager} /> */}
      </MapControl>
    </>
  );
};

export default Core;

function nextId(list: Array<{ id: number }>): number {
  let max = 0;
  if (list.length < 1) {
    return 0;
  }
  for (const object of list) {
    max = Math.max(max, object.id);
  }
  return max + 1;
}

function completePolygon(coordinates: Array<Array<Array<number>>>) {
  for (const polygon of coordinates) {
    const firstPoint = polygon[0];
    const lastPoint = polygon[polygon.length - 1];
    if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
      console.warn("Found and fixed an incomplete polygon");
      polygon.push(firstPoint);
    }
  }
}

type SiteMapSurfaceTypeWithoutId = Omit<SiteMapSurfaceType, "id"> & {
  id?: number;
};
interface CutHoleResponse {
  updatedFeature: SiteMapSurfaceType;
  newFeatures: Array<SiteMapSurfaceTypeWithoutId>;
}
/**
 * Cut a hole in a polygon by supplying another polygon which should be clipped out.
 * Supports interior cuts and overlap cuts.
 *
 * @param existingFeature
 * @param holeCoordinates
 * @returns
 */
function cutHole(
  existingFeature: SiteMapSurfaceType,
  holeCoordinates: Array<{ lat: number; lng: number }>,
): CutHoleResponse {
  const existingFeatureCoordinates: Array<Array<Array<number>>> = [];
  for (const coor of existingFeature.coordinates) {
    existingFeatureCoordinates.push(coor.map((c) => [c.lng, c.lat]));
  }
  completePolygon(existingFeatureCoordinates); // Google maps sometimes supplies polygons without the endpoint matching the first point.  Turf requires valid polygons.

  // We use turf difference function since it has nice support for all kinds of cuts (off edge, interior, between holes, etc)
  const polygon1 = turf.polygon(existingFeatureCoordinates);
  const polygon2 = turf.polygon([
    [...holeCoordinates.map((c) => [c.lng, c.lat])],
  ]);
  const diff = turf.difference(turf.featureCollection([polygon1, polygon2]))!;
  console.log("diff", diff);
  const updatedFeature: SiteMapSurfaceType = {
    ...existingFeature,
    coordinates: [],
  };
  const newFeatures: Array<SiteMapSurfaceTypeWithoutId> = [];

  // We get a MultiPolygon if we cut the original in half:
  if (diff?.geometry.type === "MultiPolygon") {
    let first = true;
    for (const coorList of diff.geometry.coordinates) {
      const newCoordinates = MapFeatureCoordinates(coorList);
      if (first) {
        updatedFeature.coordinates = newCoordinates;
        // updatedFeature = {
        //   ...newFeature,
        //   id: updatedFeature.id,
        // };
        first = false;
      } else {
        const newFeature: SiteMapSurfaceTypeWithoutId = {
          ...existingFeature,
          coordinates: newCoordinates,
        };
        delete newFeature.id;
        newFeatures.push(newFeature);
      }
    }
  } else {
    // Typically we get a single polygon (for a "normal" hole):
    updatedFeature.coordinates = MapFeatureCoordinates(
      diff.geometry.coordinates,
    );
  }
  return {
    updatedFeature,
    newFeatures,
  };
}
function MapFeatureCoordinates(coordinates: Position[][]): SimpleLatLng[][] {
  const mappedCoor: SimpleLatLng[][] = [];
  for (const coor of coordinates) {
    mappedCoor.push(
      coor.map((c) => ({ lat: c[1], lng: c[0] })) as Array<{
        lat: number;
        lng: number;
      }>,
    );
  }
  return mappedCoor;
}
